/**
 * OrgUnit Verification and Creation Logic
 * Verifies which OrgUnits exist and creates missing ones
 */

import { API, graphqlOperation } from 'aws-amplify';
import { listAreas2, getOrgUnit } from 'graphql/queries';
import { createOrgUnit } from 'graphql/mutations';

/**
 * Verify which OrgUnits exist for areas
 * @param {string} clientId - Client ID (default: 'grm')
 * @returns {object} Verification results with existing and missing OrgUnits
 */
export async function verifyOrgUnits(clientId = 'grm') {
  try {
    // Step 1: Fetch all areas from listAreas2
    const areasResponse = await API.graphql(
      graphqlOperation(listAreas2, {
        report: 'migration',
        user: null,
        role: null
      })
    );
    
    const areas = areasResponse.data.listAreas2 || [];
    
    // Filter out Ghost Kitchen if needed
    const validAreas = areas.filter(area => area.areaName !== 'Ghost Kitchen');
    
    // Step 2: Check each area for existing OrgUnit
    const verificationResults = {
      totalAreas: validAreas.length,
      existingOrgUnits: [],
      missingOrgUnits: [],
      errors: []
    };
    
    for (const area of validAreas) {
      try {
        // Try to get OrgUnit with id = area.areaId
        const orgUnitResponse = await API.graphql(
          graphqlOperation(getOrgUnit, { id: area.areaId })
        );
        
        if (orgUnitResponse.data.getOrgUnit) {
          verificationResults.existingOrgUnits.push({
            areaId: area.areaId,
            areaName: area.areaName,
            orgUnit: orgUnitResponse.data.getOrgUnit
          });
        } else {
          verificationResults.missingOrgUnits.push({
            areaId: area.areaId,
            areaName: area.areaName,
            areaManager: area.areaManager,
            areaType: area.areaType,
            storeCount: area.stores?.length || 0
          });
        }
      } catch (error) {
        // If error is "not found", add to missing
        if (error.errors?.[0]?.message?.includes('null')) {
          verificationResults.missingOrgUnits.push({
            areaId: area.areaId,
            areaName: area.areaName,
            areaManager: area.areaManager,
            areaType: area.areaType,
            storeCount: area.stores?.length || 0
          });
        } else {
          // Other errors
          verificationResults.errors.push({
            areaId: area.areaId,
            areaName: area.areaName,
            error: error.message
          });
        }
      }
    }
    
    return verificationResults;
    
  } catch (error) {
    console.error('Failed to verify OrgUnits:', error);
    throw new Error(`OrgUnit verification failed: ${error.message}`);
  }
}

/**
 * Create missing OrgUnits
 * @param {array} missingOrgUnits - Array of missing OrgUnit data
 * @param {string} clientId - Client ID (default: 'grm')
 * @returns {object} Creation results
 */
export async function createMissingOrgUnits(missingOrgUnits, clientId = 'grm') {
  const results = {
    created: [],
    failed: [],
    totalAttempted: missingOrgUnits.length
  };
  
  for (const area of missingOrgUnits) {
    try {
      const orgUnitInput = {
        id: area.areaId,
        clientId: clientId,
        orgLevel: "ORGLEVEL_1",
        name: area.areaName,
        
        // Store area manager and other info in metadata
        metadata: JSON.stringify({
          areaManager: area.areaManager,
          areaType: area.areaType,
          storeCount: area.storeCount,
          createdBy: 'migration-wizard',
          createdAt: new Date().toISOString()
        }),
        
        isActive: true
      };
      
      console.log(`Creating OrgUnit for ${area.areaName}...`);
      console.log('OrgUnit input:', JSON.stringify(orgUnitInput, null, 2));
      
      const response = await API.graphql(
        graphqlOperation(createOrgUnit, { input: orgUnitInput })
      );
      
      console.log(`✅ Successfully created OrgUnit for ${area.areaName}`);
      
      results.created.push({
        areaId: area.areaId,
        areaName: area.areaName,
        orgUnit: response.data.createOrgUnit
      });
      
    } catch (error) {
      console.error(`❌ Failed to create OrgUnit for ${area.areaName}:`, error);
      console.error('Error details:', {
        message: error.message,
        errors: error.errors,
        data: error.data,
        networkError: error.networkError
      });
      
      // Extract detailed error message
      let errorMessage = error.message;
      if (error.errors && error.errors.length > 0) {
        errorMessage = error.errors.map(e => e.message).join(', ');
      }
      
      results.failed.push({
        areaId: area.areaId,
        areaName: area.areaName,
        error: errorMessage
      });
    }
  }
  
  return results;
}

/**
 * Verify and create all missing OrgUnits in one operation
 * @param {string} clientId - Client ID (default: 'grm')
 * @returns {object} Complete verification and creation results
 */
export async function verifyAndCreateOrgUnits(clientId = 'grm') {
  console.log('🔍 Starting OrgUnit verification...');
  
  // Step 1: Verify existing OrgUnits
  const verificationResults = await verifyOrgUnits(clientId);
  
  console.log(`📊 Verification Results:`);
  console.log(`   Total Areas: ${verificationResults.totalAreas}`);
  console.log(`   Existing OrgUnits: ${verificationResults.existingOrgUnits.length}`);
  console.log(`   Missing OrgUnits: ${verificationResults.missingOrgUnits.length}`);
  console.log(`   Errors: ${verificationResults.errors.length}`);
  
  // Step 2: Create missing OrgUnits if any
  let creationResults = null;
  if (verificationResults.missingOrgUnits.length > 0) {
    console.log(`\n📝 Creating ${verificationResults.missingOrgUnits.length} missing OrgUnits...`);
    creationResults = await createMissingOrgUnits(verificationResults.missingOrgUnits, clientId);
    
    console.log(`\n✅ Creation Results:`);
    console.log(`   Successfully created: ${creationResults.created.length}`);
    console.log(`   Failed to create: ${creationResults.failed.length}`);
  }
  
  // Step 3: Re-verify to confirm all OrgUnits exist
  console.log(`\n🔄 Re-verifying OrgUnits...`);
  const finalVerification = await verifyOrgUnits(clientId);
  
  return {
    initial: verificationResults,
    creation: creationResults,
    final: finalVerification,
    allOrgUnitsExist: finalVerification.missingOrgUnits.length === 0
  };
}